// src/utils/clientsWorkspace.js
//
// Pass 50A (items 14–17, I/J/K/L) — the Clients CRM is DERIVED from canonical records (cases +
// bookings), not a separate client datastore. Client identity uses a precedence chain (a CLIENT
// is not the same thing as a booking CASE); a child booking linked to a case inherits that case's
// client identity. last/next booking are computed relative to `asOf`.

import { isClientBooking, isRetiredSuperseded } from './scheduleModel';
import { opsDateKey } from './opsTime';
// Pass 63 — ONE canonical identity contract. clientKeyOf + normalization live in clientIdentity.js so
// the read model, the New-Booking wizard, and future source adapters resolve identity identically.
import { clientKeyOf, isLowConfidenceKey } from './clientIdentity';

const str = (v) => String(v == null ? '' : v).trim();
const msOf = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate().getTime();
  if (v?.seconds) return v.seconds * 1000;
  if (v instanceof Date) return v.getTime();
  const d = new Date(v); return Number.isNaN(d.getTime()) ? null : d.getTime();
};
const timeOf = (b) => msOf(b.startAt) || (b.serviceDate ? new Date(`${b.serviceDate}T00:00:00+09:00`).getTime() : null);
const nameOf = (members, id) => { const x = members.find((m) => m.userId === id); return x ? (x.name || x.displayName || id) : id; };

// §I client identity precedence now lives in clientIdentity.clientKeyOf (single source). Re-exported
// so existing importers of the read model keep working. A name-only key stays low confidence.
export { clientKeyOf };
const isLowConfidence = (key) => isLowConfidenceKey(key);

// Derive the client roster. Cases seed clients (so an enquiry with no booking still appears);
// bookings inherit their linked case's client identity.
export function deriveClients(bookings = [], members = [], cases = [], { asOf = Date.now() } = {}) {
  const caseKeyById = new Map();
  for (const cs of cases) { const k = clientKeyOf(cs); if (k) caseKeyById.set(cs.id, k); }

  const byKey = new Map();
  const ensure = (key) => {
    let c = byKey.get(key);
    if (!c) { c = { clientKey: key, lowConfidence: isLowConfidence(key), name: '', email: '', phone: '', bookingIds: new Set(), caseIds: new Set(), sessions: 0, lastBookingAt: null, nextBookingAt: null, lastInstructorId: '', lastInstructorName: '', nextInstructorId: '', nextInstructorName: '' }; byKey.set(key, c); }
    return c;
  };
  const mergeContact = (c, rec) => {
    c.name = c.name || str(rec.requesterName || rec.leadGuest?.fullName);
    c.email = c.email || str(rec.requesterEmail || rec.leadGuest?.email);
    c.phone = c.phone || str(rec.requesterPhone || rec.leadGuest?.phone);
  };

  // §L cases first (enquiry with no operational booking still becomes a client)
  for (const cs of cases) {
    const key = clientKeyOf(cs); if (!key) continue;
    const c = ensure(key); mergeContact(c, cs); c.caseIds.add(cs.id);
  }

  for (const b of bookings) {
    if (!isClientBooking(b)) continue;
    // Pass 58G2 — a RETIRED/SUPERSEDED booking (e.g. the 26/27 bootstrap placeholders with fabricated
    // guest names) must NOT seed or contribute to a visible canonical client. The booking doc is retained
    // as history; a legitimate client established independently by a case still stands.
    if (isRetiredSuperseded(b)) continue;
    const key = (b.bookingRequestId && caseKeyById.has(b.bookingRequestId)) ? caseKeyById.get(b.bookingRequestId) : clientKeyOf(b);
    if (!key) continue;
    const c = ensure(key); mergeContact(c, b); c.bookingIds.add(b.id);
    if (b.bookingRequestId) c.caseIds.add(b.bookingRequestId);
    if (b.status === 'canceled') continue;
    c.sessions += 1;
    const t = timeOf(b);
    if (t == null) continue;
    if (t < asOf) {
      if (c.lastBookingAt == null || t > c.lastBookingAt) { c.lastBookingAt = t; if (b.instructorId) { c.lastInstructorId = b.instructorId; c.lastInstructorName = b.instructorNameSnapshot || nameOf(members, b.instructorId); } }
    } else {
      if (c.nextBookingAt == null || t < c.nextBookingAt) { c.nextBookingAt = t; if (b.instructorId) { c.nextInstructorId = b.instructorId; c.nextInstructorName = b.instructorNameSnapshot || nameOf(members, b.instructorId); } }
    }
  }

  return [...byKey.values()]
    .map((c) => ({ ...c, bookingIds: [...c.bookingIds], caseIds: [...c.caseIds], totalSessions: c.sessions, cases: c.caseIds.size }))
    .sort((a, b) => (b.lastBookingAt || b.nextBookingAt || 0) - (a.lastBookingAt || a.nextBookingAt || 0));
}

// Pass 58G1 — "Active this season" as an ACTIVITY PROPERTY of the ONE canonical client index (never a
// second identity universe). A canonical client (from deriveClients WITH cases) is active if any of its
// linked bookingIds is a live client booking in `seasonId`. This preserves the exact client identity the
// full index established (a case-linked child booking cannot fall back to a weaker key and "disappear").
// Overview and Clients → Active must call THIS with the same full client roster so the counts match.
export function activeSeasonClients(clients = [], seasonBookings = []) {
  const liveIds = new Set(
    (seasonBookings || []).filter((b) => isClientBooking(b)).map((b) => b.id)
  );
  return (clients || []).filter((c) => (c.bookingIds || []).some((id) => liveIds.has(id)));
}

// §17 follow-up / retention signal: past activity, no upcoming booking, last booking older than
// `staleDays`. Pure + reusable (does not duplicate the retention engine's own calculations).
export function followUpClients(clients = [], { asOf = Date.now(), staleDays = 30 } = {}) {
  const cutoff = asOf - staleDays * 24 * 60 * 60 * 1000;
  return clients.filter((c) => c.lastBookingAt && !c.nextBookingAt && c.lastBookingAt < cutoff);
}

export { opsDateKey };
